import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { useAuth } from "../useAuth";
import { Edit2 } from "lucide-react";
import { dbLoadSessionsByCourse, mapTeacherSessionRow } from "../lib/db";
import { TRATEditor, SessionActionsMenu, useToast, Toast } from "./TRATEditor";
import { SESSION_STATUSES, sessionStatus, statusMeta } from "../lib/status";

// ─────────────────────────────────────────
//  COURSE VIEW SCREEN
// ─────────────────────────────────────────
function CourseViewScreen({ course, onBack, onCreateTRAT, onDashboard, lang, G, css, isSuperAdmin }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  // Klick på en tRAT öppnar samma editor som "Mina tRAT:ar" på startsidan.
  // Tidigare var raderna helt inerta om tRAT:en inte hade några grupper —
  // en schemalagd tRAT gick alltså inte att öppna och redigera härifrån.
  const [viewingSession, setViewingSession] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [toast, showToast] = useToast();
  const [editing, setEditing] = useState(false);
  const [editCourseName, setEditCourseName] = useState("");
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const sv = lang === "sv";

  const program = course?.programs;
  const org = program?.organisations;
  const breadcrumb = [org?.name, program?.name].filter(Boolean);

  // Terminsetikett för en tRAT, från dess (automatiskt satta) kurstillfälle
  function termLabel(s) {
    const inst = s?._instance;
    if (!inst) return null;
    return inst.name || [inst.term, inst.year].filter(Boolean).join(" ") || null;
  }

  useEffect(() => {
    if (!course) return;
    setLoading(true);
    dbLoadSessionsByCourse(course.id).then(data => {
      setSessions(data);
      setLoading(false);
    });
  }, [course, reloadKey]);

  // Group by the shared status model (src/lib/status.js) so the words match
  // Översikt, tRAT-listan och dashboarden. The old ad-hoc filters also had a
  // gap: a locked session with active groups fell through to "Ej startade".
  const byStatus = {};
  for (const s of sessions) {
    const st = sessionStatus({
      locked: s.session_locked,
      availableAt: s.available_at,
      activeGroups: s._stats?.active || 0,
      doneGroups: s._stats?.completed || 0,
    });
    if (!byStatus[st]) byStatus[st] = [];
    byStatus[st].push(s);
  }

  function formatDate(iso) {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString(sv ? "sv-SE" : "en-US", { day: "numeric", month: "short", year: "numeric" });
  }

  function avgLabel(stats) {
    if (!stats || stats.avgScore === null || stats.completed === 0) return null;
    return `${stats.avgScore.toFixed(1)} / ${stats.maxScore || "?"} p`;
  }

  function startEditing() {
    setEditCourseName(course?.name || "");
    setEditing(true);
  }

  async function saveEdits() {
    setSaving(true);
    try {
      if (editCourseName.trim() && editCourseName !== course?.name) {
        await supabase.from("courses").update({ name: editCourseName.trim() }).eq("id", course.id);
        if (course) course.name = editCourseName.trim();
      }
      setEditing(false);
    } catch (e) {
      console.error("saveEdits:", e);
    }
    setSaving(false);
  }

  // Section component to avoid repetition
  function SessionSection({ title, items, accent, showAvg, isLive }) {
    if (!items || items.length === 0) return null;
    return (
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: accent, flexShrink: 0 }} />
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: G.muted }}>{title}</span>
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: G.muted, background: G.surface2, borderRadius: 5, padding: "1px 6px" }}>{items.length}</span>
        </div>
        <div style={{ display: "grid", gap: 6 }}>
          {items.map(s => {
            const stats = s._stats || {};
            const avg = showAvg ? avgLabel(stats) : null;
            const isActive = !!isLive;
            return (
              <div
                key={s.id}
                role="button"
                tabIndex={0}
                title={sv ? "Öppna tRAT" : "Open tRAT"}
                onClick={e => { if (e.target.closest("button") || e.target.closest(".no-click")) return; setViewingSession(mapTeacherSessionRow(s)); }}
                onKeyDown={e => { if (e.target !== e.currentTarget) return; if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setViewingSession(mapTeacherSessionRow(s)); } }}
                style={{ background: G.surface, border: `1px solid ${isActive ? accent + "55" : G.border}`, borderRadius: 12, padding: "14px 18px", display: "flex", alignItems: "center", gap: 14, transition: "border-color 0.15s", cursor: "pointer" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = accent + "88"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = isActive ? accent + "55" : G.border; }}
              >
                {/* Active stripe */}
                {isActive && <div style={{ width: 3, height: 32, borderRadius: 2, background: accent, flexShrink: 0 }} />}

                {/* Main info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: 14, color: G.text, marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {s.session_title || s.id}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: G.muted }}>{formatDate(s.created_at)}</span>
                    <span style={{ fontSize: 12, color: G.muted }}>{s.num_questions} {sv ? "frågor" : "questions"}</span>
                    {isActive && stats.active > 0 && (
                      <span style={{ fontSize: 12, color: accent, fontWeight: 500 }}>
                        {stats.active} {sv ? "aktiva grupper" : "active groups"}
                        {stats.completed > 0 ? ` · ${stats.completed} ${sv ? "klara" : "done"}` : ""}
                      </span>
                    )}
                    {!isActive && stats.completed > 0 && (
                      <span style={{ fontSize: 12, color: G.muted }}>
                        {stats.completed} {sv ? "grupper" : "groups"}
                      </span>
                    )}
                    {avg && (
                      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: G.accent, background: G.accent + "14", borderRadius: 5, padding: "1px 7px" }}>
                        {sv ? "snitt" : "avg"} {avg}
                      </span>
                    )}
                  </div>
                </div>

                {/* Code chip */}
                {termLabel(s) && (
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: G.muted, background: G.surface2, border: `1px solid ${G.border}`, borderRadius: 5, padding: "2px 7px", flexShrink: 0 }}>
                    {termLabel(s)}
                  </span>
                )}
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: G.gold, background: G.gold + "14", border: `1px solid ${G.gold}22`, borderRadius: 6, padding: "2px 8px", flexShrink: 0 }}>
                  {s.id}
                </span>

                {/* Dashboard link for active/completed */}
                {(isActive || stats.completed > 0) && s.dash_id && (
                  <button
                    onClick={() => onDashboard && onDashboard(s.dash_id)}
                    style={{ background: isActive ? accent + "18" : G.surface2, border: `1px solid ${isActive ? accent + "44" : G.border}`, borderRadius: 8, color: isActive ? accent : G.text, fontSize: 13, fontWeight: 500, padding: "6px 12px", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, transition: "all 0.15s" }}
                    onMouseEnter={e => { e.currentTarget.style.background = isActive ? accent + "28" : G.surface2; }}
                    onMouseLeave={e => { e.currentTarget.style.background = isActive ? accent + "18" : G.surface2; }}
                  >
                    Dashboard →
                  </button>
                )}
                <SessionActionsMenu
                  session={mapTeacherSessionRow(s)}
                  onView={() => setViewingSession(mapTeacherSessionRow(s))}
                  onChanged={() => setReloadKey(k => k + 1)}
                  notify={showToast}
                  lang={lang}
                  G={G}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: G.bg, color: G.text }}>

      {viewingSession && (
        <TRATEditor
          session={viewingSession}
          onClose={() => setViewingSession(null)}
          onSave={() => { setViewingSession(null); setReloadKey(k => k + 1); }}
          onRefresh={() => setReloadKey(k => k + 1)}
          lang={lang}
          G={G}
          css={css}
        />
      )}


      {/* Sub-header */}
      <div style={{ background: G.surface, borderBottom: `1px solid ${G.border}`, padding: "0 20px" }}>
        {/* Höjden är fast i vanligt läge men växer under redigering: med fast
            56px blev det wrappade formuläret vertikalt centrerat i en för låg
            box och spillde upp bakom den fixerade topbaren. */}
        <div style={{
          maxWidth: 860, margin: "0 auto", display: "flex", alignItems: "center",
          gap: 12, flexWrap: "wrap",
          minHeight: 56, ...(editing ? { padding: "10px 0" } : { height: 56 }),
        }}>
          {/* Back */}
          <button
            onClick={onBack}
            style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", color: G.muted, cursor: "pointer", fontSize: 14, padding: 0, flexShrink: 0 }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {sv ? "Kurser" : "Courses"}
          </button>

          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ color: G.border, flexShrink: 0 }}>
            <path d="M4 2l6 5-6 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
          </svg>

          {/* Breadcrumb: program if exists */}
          {breadcrumb.length > 0 && (
            <>
              <span style={{ fontSize: 13, color: G.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 160 }}>
                {breadcrumb.join(" › ")}
              </span>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ color: G.border, flexShrink: 0 }}>
                <path d="M4 2l6 5-6 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
              </svg>
            </>
          )}

          {/* Course + instance */}
          {editing ? (
            <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <input
                value={editCourseName}
                onChange={e => setEditCourseName(e.target.value)}
                placeholder={sv ? "Kursnamn" : "Course name"}
                autoFocus
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 14, color: G.text, background: G.surface2, border: `1px solid ${G.border}`, borderRadius: 6, padding: "4px 10px", minWidth: 120, flex: "1 1 160px", outline: "none" }}
              />
              <button onClick={saveEdits} disabled={saving} style={{ background: G.accent, color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, padding: "5px 12px", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}>
                {saving ? "…" : (sv ? "Spara" : "Save")}
              </button>
              <button onClick={() => setEditing(false)} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 6, color: G.muted, fontSize: 12, padding: "5px 10px", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}>
                {sv ? "Avbryt" : "Cancel"}
              </button>
            </div>
          ) : (
            <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "baseline", gap: 10, overflow: "hidden" }}>
              <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 15, color: G.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {course?.name}
              </span>
              {/* Kursnamn hanteras av admin (RLS begränsar skrivning till
                  superadmin), så bara de får redigera. */}
              {isSuperAdmin && (
                <button onClick={startEditing} style={{ background: "none", border: "none", color: G.muted, cursor: "pointer", padding: "2px 4px", flexShrink: 0, display: "flex", alignItems: "center" }} title={sv ? "Redigera" : "Edit"}>
                  <Edit2 size={13} />
                </button>
              )}
            </div>
          )}

          {/* New tRAT CTA */}
          <button
            onClick={() => onCreateTRAT(course)}
            style={{ background: G.accent, color: "#fff", border: "none", borderRadius: 8, fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 13, padding: "8px 16px", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}
          >
            + {sv ? "Ny tRAT" : "New tRAT"}
          </button>
        </div>
      </div>

      {/* Main content */}
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "32px 20px" }}>

        {/* Loading */}
        {loading && (
          <div style={{ display: "flex", gap: 10, alignItems: "center", color: G.muted, fontSize: 14 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ animation: "spin 1s linear infinite" }}>
              <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" strokeDasharray="28" strokeDashoffset="10"/>
            </svg>
            {sv ? "Laddar…" : "Loading…"}
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        )}

        {/* Empty state */}
        {!loading && sessions.length === 0 && (
          <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 16, padding: "52px 32px", textAlign: "center", maxWidth: 480, margin: "0 auto" }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: G.surface2, border: `1px solid ${G.border}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                <rect x="2" y="3" width="18" height="16" rx="3" stroke={G.muted} strokeWidth="1.3"/>
                <path d="M8 11h6M11 8v6" stroke={G.muted} strokeWidth="1.3" strokeLinecap="round"/>
              </svg>
            </div>
            <p style={{ ...css.heading, fontSize: 16, color: G.text, marginBottom: 8 }}>
              {sv ? "Inga tRATs ännu" : "No tRATs yet"}
            </p>
            <p style={{ color: G.muted, fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
              {sv
                ? "Skapa den första tRAT:en för denna kurs."
                : "Create the first tRAT for this course."}
            </p>
            <button
              onClick={() => onCreateTRAT(course)}
              style={{ ...css.btnPrimary, padding: "10px 24px", display: "inline-block" }}
            >
              + {sv ? "Skapa tRAT" : "Create tRAT"}
            </button>
          </div>
        )}

        {/* Session sections */}
        {!loading && sessions.length > 0 && (
          <>
            {SESSION_STATUSES.map(st => {
              const meta = statusMeta(st, lang, G);
              return (
                <SessionSection
                  key={st}
                  title={meta.labelPlural}
                  items={byStatus[st]}
                  accent={meta.color}
                  showAvg={st === "done" || st === "locked"}
                  isLive={st === "live"}
                />
              );
            })}
          </>
        )}
      </div>
      <Toast toast={toast} G={G} />
    </div>
  );
}

export { CourseViewScreen };
